import { useState, useEffect } from 'react'
import moment from 'moment'

import { CoreApi } from '/src/helpers/CoreApi'
import useDebounce from '/src/hooks/useDebounce'
import Title from '/src/components/@common.blocks/Title'
import Container from '/src/components/@common.blocks/Container'
import FormField from '/src/components/@common.blocks/FormField'
import Input from '/src/components/@common.blocks/Input'
import Select from '/src/components/@common.blocks/Select'
import UpcomingCard from '/src/components/@common.blocks/UpcomingCard'
import Grid from '/src/components/@common.blocks/Grid'
import useInfiniteScroll from '/src/hooks/useInfiniteScroll'

const SIZE_PER_PAGE = 10

const groupArrayByDate = (prevState, newState) => {
  const data = prevState || {}
  const today = moment().format('MMMM D')
  for (const item of newState) {
    let saleDate = moment(item.saleDate).format('MMMM D')
    if (saleDate === today) {
      saleDate = 'Today'
    }
    if (!data[saleDate]) {
      data[saleDate] = [{ ...item }]
    } else {
      data[saleDate].push({ ...item })
    }
  }
  return data
}

const DropsCalendar = () => {
  const [groupedData, setGroupedData] = useState([])
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState({
    saleDateFrom: moment().format('YYYY-MM-DD'),
    priceMin: 0,
    priceMax: 500,
    supplyMin: 0,
    supplyMax: 10000,
  })

  const [loading, setLoading] = useInfiniteScroll(fetchData)
  const query = useDebounce(filter, 500)

  async function fetchData(initialFetch = false) {
    const newPage = initialFetch ? 1 : page + 1
    const params = { ...query }
    if (params.saleDateFrom) {
      params.saleDateFrom = new Date(query.saleDateFrom).getTime()
    }
    params.priceMin = params.priceMin ? Number(params.priceMin) : 0
    params.priceMax = params.priceMax ? Number(params.priceMax) : 500

    params.supplyMin = params.supplyMin ? Number(params.supplyMin) : 0
    params.supplyMax = params.supplyMax ? Number(params.supplyMax) : 10000

    const data = await CoreApi.getUpcomingCollections({
      ...params,
      page: newPage,
      size: SIZE_PER_PAGE,
    })

    setGroupedData((prevState) => {
      setPage(newPage)
      setLoading(false)
      return initialFetch
        ? groupArrayByDate({}, data)
        : groupArrayByDate(prevState, data)
    })
  }

  useEffect(() => {
    fetchData(true).catch((error) => {
      console.log(error)
    })
  }, [query])

  const onChange = (e) => {
    let value = e.target.value
    let name = e.target.name
    if (name === 'blockchain' && value === 'all') {
      value = undefined
    }
    setFilter((prevState) => ({ ...prevState, [name]: value }))
  }

  return (
    <section className='drops-calendar' id='drops-calendar'>
      <Container>
        <Title level={2} className='drops-calendar__title'>
          Drops Calendar
        </Title>
        <div className='drops-calendar__header'>
          <Title level={3}>Filter</Title>
          <div className='drops-calendar__filter'>
            <FormField title='Sale Date'>
              <Input
                name='saleDateFrom'
                value={filter.saleDateFrom}
                type='date'
                onChange={onChange}
              />
            </FormField>
            <FormField title='Blockchain'>
              <Select
                options={[
                  { title: 'All', value: 'all' },
                  { title: 'Ethereum', value: 'ethereum' },
                  { title: 'Solana', value: 'solana' },
                  { title: 'Polygon (Matic)', value: 'polygon' },
                  { title: 'Cardano', value: 'cardano' },
                  { title: 'Binance Smart Chain', value: 'bsc' },
                ]}
                name='blockchain'
                value={filter.blockchain}
                onSelect={onChange}
              />
            </FormField>
            <FormField title='Sale Price'>
              <div className='drops-calendar__filter-range'>
                <Input
                  name='priceMin'
                  type='number'
                  value={filter.priceMin}
                  onChange={onChange}
                />
                <Input
                  name='priceMax'
                  type='number'
                  value={filter.priceMax}
                  onChange={onChange}
                />
              </div>
            </FormField>
            <FormField title='Supply'>
              <div className='drops-calendar__filter-range'>
                <Input
                  name='supplyMin'
                  type='number'
                  value={filter.supplyMin}
                  onChange={onChange}
                />
                <Input
                  name='supplyMax'
                  type='number'
                  value={filter.supplyMax}
                  onChange={onChange}
                />
              </div>
            </FormField>
          </div>
          <div className='drops-calendar__content'>
            {Object.keys(groupedData).map((title) => (
              <div key={title} className='drops-calendar__day'>
                <Title level={3}>{title}</Title>
                <Grid gutter={{ xs: 2, sm: 3 }}>
                  {groupedData[title].map((item, key) => (
                    <Grid.Col key={key} xs={6} sm={4} xl={3}>
                      <UpcomingCard key={key} collection={item} />
                    </Grid.Col>
                  ))}
                </Grid>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}

export default DropsCalendar
